import type { RefObject } from 'react'
import type { ScannerStatus } from '../../scanner/useBarcodeScanner'
import styles from './CameraViewfinder.module.css'

interface CameraViewfinderProps {
  size: 'tall' | 'strip' | 'basket' | 'assign'
  /** «دوربین فعال» on the pallet-identify screen, «اسکن پیوسته فعال» on the detail screen. */
  badgeLabel: string
  hint: string
  status: ScannerStatus
  error: string | null
  videoRef: RefObject<HTMLVideoElement | null>
  /** Tapping the frame is a scan trigger in the artboard; it retries a failed camera here. */
  onActivate: () => void
  /** Basket and assignment artboards omit the camera status badge. */
  showBadge?: boolean
}

export function CameraViewfinder({
  size,
  badgeLabel,
  hint,
  status,
  error,
  videoRef,
  onActivate,
  showBadge = true,
}: CameraViewfinderProps) {
  const running = status === 'running'
  const faulted = error !== null

  return (
    <button
      type="button"
      className={`${styles.frame} ${styles[size]}`}
      onClick={onActivate}
      aria-label={badgeLabel}
    >
      <video ref={videoRef} className={styles.video} muted playsInline />

      {showBadge && (
        <span className={`${styles.badge} ${running ? '' : styles.badgeWarn}`}>
          <span className={styles.dot} />
          {running ? badgeLabel : statusLabel(status)}
        </span>
      )}

      {/*
        The reticle marks the region the decoder actually reads — the scan loop crops to
        the same fraction, so what the operator aims at is what gets decoded.
      */}
      {running && (
        <span className={styles.reticle}>
          <span className={styles.sweep} />
        </span>
      )}

      {faulted ? <span className={styles.fault}>{error}</span> : <span className={styles.hint}>{hint}</span>}
    </button>
  )
}

function statusLabel(status: ScannerStatus): string {
  switch (status) {
    case 'starting':
      return 'در حال روشن شدن دوربین'
    case 'denied':
      return 'دسترسی دوربین رد شد'
    case 'unavailable':
      return 'دوربین در دسترس نیست'
    case 'error':
      return 'خطای دوربین'
    default:
      return 'دوربین خاموش'
  }
}
